import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

const PasswordScreen = ({ onAuthenticated, translations, language, onLanguageChange }) => {
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPrivacyText, setShowPrivacyText] = useState(false);

  const t = translations[language];
  const AUTH_ENDPOINT = (process.env.REACT_APP_API_ENDPOINT || 'https://sarinakaegi.ch/api/rsvps').replace('/rsvps', '/auth/login');

  // Disable scrolling when login screen is shown
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError(false);

    try {
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: passwordInput })
      });

      const data = await response.json();

      if (response.ok && data.success && data.token) {
        // Store the JWT token
        localStorage.setItem('birthday_token', data.token);
        setPasswordError(false);
        onAuthenticated();
      } else {
        setPasswordError(true);
        setPasswordInput('');
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setPasswordError(true);
      setPasswordInput('');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center px-4">
      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={onLanguageChange}
          className="bg-white px-4 py-2 rounded-full shadow-lg border-2 border-purple-200 hover:border-purple-400 transition-all"
        >
          {t.switchLang}
        </button>
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-4 border-purple-100">
          <div className="text-center mb-8">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-purple-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-purple-800 mb-2">{t.passwordTitle}</h1>
            <p className="text-gray-600">{t.passwordSubtitle}</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {passwordError && (
              <div className="p-4 bg-red-100 border-2 border-red-300 rounded-xl text-red-800 text-center">
                {t.passwordError}
              </div>
            )}

            <div>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder={t.passwordPlaceholder}
                className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:outline-none text-lg"
                autoFocus
                required
              />
            </div>

            <button
              type="submit"
              disabled={passwordLoading}
              className="w-full bg-purple-600 text-white font-bold py-4 rounded-xl hover:bg-purple-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {passwordLoading ? t.passwordSubmitting : t.passwordSubmit}
            </button>
          </form>
        </div>

        {/* Privacy Notice Footer */}
        <div className="mt-6 bg-white rounded-2xl shadow-xl p-4 border-2 border-purple-100">
          <button
            type="button"
            onClick={() => setShowPrivacyText(!showPrivacyText)}
            className="w-full text-left font-semibold text-purple-800 hover:text-purple-600 transition-colors flex items-center justify-between"
          >
            <span className="text-sm">{t.privacyTitle}</span>
            <span className="text-xs text-purple-600">
              {showPrivacyText ? t.privacyShowLess : t.privacyShowMore}
            </span>
          </button>

          {showPrivacyText && (
            <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200 text-xs text-gray-700 leading-relaxed">
              {t.privacyText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordScreen;
